import React, { useState, useMemo } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useGetProfile } from '../hooks/useProfile.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import {
  useSubgroups,
  useSubgroup,
  useCreateSubgroup,
  useUpdateSubgroup,
  useDeleteSubgroup,
  useExportSubgroups,
  useExportSubgroupDetail,
  useImportSubgroups,
  useDownloadSubgroupTemplate,
} from '../hooks/useSubgroups.ts';
import { useDebounce } from '../hooks/useDebounce.ts';
import { useMembers, useUpdateMember } from '../hooks/useMembers.ts';
import { Plus, Edit, Trash, Eye, ArrowLeft, Layers, Users, CreditCard, Calendar, FileText, UserPlus, UserX, UserCheck, Download, Upload, Info, Search, Globe, CheckCircle } from 'lucide-react';
import Modal from '../components/Modal.tsx';
import Table from '../components/Table.tsx';
import AsyncMemberSelect from '../components/ui/AsyncMemberSelect.tsx';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';
import api from '../utils/axios.ts';

export default function SubgroupsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: profile, isLoading: isProfileLoading } = useGetProfile();
  const isAdmin = useIsAdmin();
  const currentGroup = profile?.data?.current_group;
  const isHouseManagement = Number(currentGroup?.is_house_management) === 1;
  const isIrregular = Number(currentGroup?.is_irregular) === 1;
  const villaLabel = "Subgroup";
  const tenantLabel = "Member";

  const activeTab = searchParams.get('tab') === 'villages' ? 'villages' : 'subgroups';

  const setActiveTab = (tab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
    setPage(1);
  };

  const userRole = profile?.data?.current_role || profile?.data?.role?.name || profile?.data?.user?.role?.name;
  const isVillageAdmin = userRole === 'village_admin';
  const canAccessSubgroups = isAdmin || isVillageAdmin;

  // Defer the redirect render so it doesn't sit between hook calls (rules-of-hooks).
  const shouldRedirect = !isProfileLoading && profile?.data && !canAccessSubgroups;

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data: subgroupsData, isLoading: isListLoading } = useSubgroups({ search: debouncedSearchQuery });
  const subgroups = subgroupsData?.data || [];

  const [selectedId, setSelectedId] = useState(null);
  const { data: detailData, isLoading: isDetailLoading } = useSubgroup(selectedId);
  const detail = detailData?.data || null;

  // Filter subgroups by activeTab if irregular group
  const filteredSubgroups = useMemo(() => {
    if (!isIrregular) return subgroups;
    return subgroups.filter(sub => {
      if (activeTab === 'villages') {
        return sub.type === 'village';
      }
      return sub.type === 'subgroup' || !sub.type;
    });
  }, [subgroups, isIrregular, activeTab]);

  // Pagination state
  const [page, setPage] = useState(1);
  const perPage = 10;

  const paginatedSubgroups = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredSubgroups.slice(start, start + perPage);
  }, [filteredSubgroups, page]);

  // Modals state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingSubgroup, setEditingSubgroup] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('subgroup');

  // Mutations
  const createMutation = useCreateSubgroup();
  const updateMutation = useUpdateSubgroup();
  const deleteMutation = useDeleteSubgroup();
  const exportAllMutation = useExportSubgroups();
  const exportDetailMutation = useExportSubgroupDetail();
  const importMutation = useImportSubgroups();
  const templateMutation = useDownloadSubgroupTemplate();
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportAll = async () => {
    const blob = await exportAllMutation.mutateAsync();
    downloadBlob(blob, `subgroup_management_report.csv`);
  };

  const handleExportDetail = async (sub) => {
    const blob = await exportDetailMutation.mutateAsync(sub.id);
    downloadBlob(blob, `subgroup_${(sub.name || 'detail').replace(/\s+/g, '_')}_ledger.csv`);
  };

  const handleDownloadTemplate = async () => {
    const blob = await templateMutation.mutateAsync();
    downloadBlob(blob, `subgroup_import_template.csv`);
  };

  const handleImportSubmit = () => {
    if (!importFile) {
      toast.error('Please choose a CSV file');
      return;
    }
    importMutation.mutate(importFile, {
      onSuccess: () => {
        setImportFile(null);
        setIsImportOpen(false);
      },
    });
  };

  // Tenant (subgroup member) management
  const queryClient = useQueryClient();
  const updateMemberMutation = useUpdateMember();
  const [isTenantModalOpen, setIsTenantModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [tenantForm, setTenantForm] = useState({ member_id: '', subgroup_id: '', selectedOption: null });
  const [showAllMilestones, setShowAllMilestones] = useState(false);

  const openAddTenant = () => {
    navigate('/members');
  };

  const openEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setTenantForm({
      member_id: tenant.id.toString(),
      subgroup_id: selectedId.toString(),
    });
    setIsTenantModalOpen(true);
  };

  const handleTenantSubmit = (e) => {
    e.preventDefault();
    if (!tenantForm.member_id) {
      return toast.error(`Please select a ${tenantLabel}`);
    }
    
    const member = tenantForm.selectedOption?.rawMember;
    if (!member && !editingTenant) {
      return toast.error('Selected member not found');
    }

    const payload = {
      id: tenantForm.member_id,
      subgroup_id: tenantForm.subgroup_id !== undefined ? tenantForm.subgroup_id : selectedId,
    };

    if (member) {
      payload.name = member.name;
      payload.phone_number = member.phone_number;
      payload.email = member.email || '';
      payload.member_no = member.member_no || '';
    } else if (editingTenant) {
      payload.name = editingTenant.name;
      payload.phone_number = editingTenant.phone_number;
      payload.email = editingTenant.email || '';
      payload.member_no = editingTenant.member_no || '';
    }

    updateMemberMutation.mutate(
      payload,
      {
        onSuccess: () => { 
          toast.success(`${tenantLabel} assigned successfully`);
          queryClient.invalidateQueries({ queryKey: ['subgroup', selectedId] });
          queryClient.invalidateQueries({ queryKey: ['subgroups'] });
          setIsTenantModalOpen(false);
          setEditingTenant(null);
        },
        onError: (err) => toast.error(getApiErrorMessage(err)),
      }
    );
  };

  const removeFromSubgroup = (tenant) => {
    if (!window.confirm(`Remove ${tenant.name} from this subgroup? They will not be deleted from the system.`)) return;
    updateMemberMutation.mutate(
      {
        id: tenant.id,
        name: tenant.name,
        phone_number: tenant.phone_number,
        email: tenant.email,
        member_no: tenant.member_no,
        subgroup_id: '',
      },
      {
        onSuccess: () => {
          toast.success(`${tenantLabel} removed from subgroup`);
          queryClient.invalidateQueries({ queryKey: ['subgroup', selectedId] });
          queryClient.invalidateQueries({ queryKey: ['subgroups'] });
        },
        onError: (err) => toast.error(getApiErrorMessage(err)),
      }
    );
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error(`Subgroup Name/No is required`);
    createMutation.mutate(
      { name, description, type },
      {
        onSuccess: () => {
          setIsCreateOpen(false);
          setName('');
          setDescription('');
          setType('subgroup');
        },
      }
    );
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error(`Subgroup Name/No is required`);
    updateMutation.mutate(
      { id: editingSubgroup.id, name, description, type },
      {
        onSuccess: () => {
          setIsEditOpen(false);
          setEditingSubgroup(null);
          setName('');
          setDescription('');
          setType('subgroup');
        },
      }
    );
  };

  const handleDelete = (id) => {
    if (window.confirm(`Are you sure you want to delete this subgroup? Members will be unassigned but NOT deleted.`)) {
      deleteMutation.mutate(id, {
        onSuccess: () => {
          if (selectedId === id) {
            setSelectedId(null);
          }
        }
      });
    }
  };

  const openEditModal = (sub) => {
    setEditingSubgroup(sub);
    setName(sub.name);
    setDescription(sub.description || '');
    setType(sub.type || 'subgroup');
    setIsEditOpen(true);
  };

  const openCreateModal = () => {
    setName('');
    setDescription('');
    setType(activeTab === 'villages' ? 'village' : 'subgroup');
    setIsCreateOpen(true);
  };

  // Main List Columns
  const listColumns = [
    {
      label: 'Subgroup Name/No',
      field: 'name',
      render: (row) => (
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${row.type === 'village' ? 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400' : 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'}`}>
            {row.type === 'village' ? <Globe size={20} /> : <Layers size={20} />}
          </div>
          <div>
            <span className="font-semibold text-gray-900 dark:text-white block">{row.name}</span>
            {row.description && <span className="text-xs text-gray-500 dark:text-gray-400 block max-w-xs truncate">{row.description}</span>}
          </div>
        </div>
      ),
    },
    {
      label: 'Type',
      field: 'type',
      render: (row) => (
        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
          row.type === 'village'
            ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400'
            : 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
        }`}>
          {row.type === 'village' ? 'Village / Region' : 'Subgroup / Category'}
        </span>
      ),
    },
    {
      label: `${tenantLabel}s Count`,
      field: 'members_count',
      render: (row) => (
        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400">
          {row.members_count || 0} {tenantLabel}s
        </span>
      ),
    },


    {
      label: 'Actions',
      field: 'actions',
      render: (row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setSelectedId(row.id)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            title="View Details"
          >
            <Eye size={18} />
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => openEditModal(row)}
                className="p-1.5 rounded-lg text-gray-500 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                title="Edit"
              >
                <Edit size={18} />
              </button>
              <button
                onClick={() => handleDelete(row.id)}
                className="p-1.5 rounded-lg text-gray-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                title="Delete"
              >
                <Trash size={18} />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  // All hooks above this point — render-time guards only from here on.
  if (shouldRedirect) {
    return <Navigate to="/my-subgroup" replace />;
  }

  if (selectedId && detail) {
    const membersColumns = [
      {
        label: 'Name',
        field: 'name',
        render: (m) => (
          <div>
            <span className="font-medium text-gray-900 dark:text-white block">{m.name}</span>
            {m.member_no && <span className="text-xs text-gray-500 block">No. {m.member_no}</span>}
          </div>
        ),
      },
      {
        label: 'Phone Number',
        field: 'phone_number',
        render: (m) => <span>{m.phone_number || '-'}</span>,
      },
      {
        label: 'Email Address',
        field: 'email',
        render: (m) => <span className="text-gray-500">{m.email || '-'}</span>,
      },


      {
        label: 'Actions',
        field: 'actions',
        render: (m) => (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => openEditTenant(m)}
              className="p-1.5 rounded-lg text-gray-500 hover:text-amber-600 hover:bg-amber-50 transition-colors"
              title={`Edit ${tenantLabel}`}
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => removeFromSubgroup(m)}
              className="p-1.5 rounded-lg transition-colors text-gray-500 hover:text-rose-600 hover:bg-rose-50"
              title={`Remove ${tenantLabel} from Subgroup`}
            >
              <UserX size={16} />
            </button>
          </div>
        ),
      },
    ];


    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Detail Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSelectedId(null)}
              className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <div className="flex items-center space-x-2">
                <Layers size={28} className="text-blue-600" />
                <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">{detail.subgroup.name}</h1>
              </div>
              <p className="text-sm text-gray-500 mt-1">{detail.subgroup.description || `Overview and consolidated statement of ${detail.subgroup.name}.`}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={async () => {
                try {
                  const params = {};
                  if (detail.subgroup.type === 'village') {
                    params.village_id = detail.subgroup.id;
                  } else {
                    params.subgroup_id = detail.subgroup.id;
                  }
                  const res = await api.get('/contributions/matrix-export', { params, responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `matrix_breakdown_${detail.subgroup.name.toLowerCase().replace(/\s+/g, '_')}.csv`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('Matrix Breakdown exported');
                } catch (error) {
                  toast.error(getApiErrorMessage(error) || 'Export failed');
                }
              }}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium transition-colors flex items-center space-x-2 shadow-sm"
              title="Export Drive Matrix Breakdown for this subgroup/village"
            >
              <Download size={16} />
              <span>Export Matrix Breakdown</span>
            </button>
            <button
              onClick={() => openEditModal(detail.subgroup)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors flex items-center space-x-2"
            >
              <Edit size={16} />
              <span>Edit Details</span>
            </button>
            <button
              onClick={() => handleDelete(detail.subgroup.id)}
              className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 font-medium transition-colors flex items-center space-x-2 shadow-sm"
            >
              <Trash size={16} />
              <span>Delete Subgroup</span>
            </button>
          </div>
        </div>

        {/* Stats summary */}
        {(() => {
          const driveSummary = detail.irregular_drive_summary || { drives: [], total_target: 0, total_paid: 0, total_arrears: 0 };
          
          const subgroupDriveColumns = [
            {
              label: 'Drive / Category',
              field: 'title',
              render: (row) => (
                <div>
                  <span className="font-bold text-gray-900 dark:text-white block">{row.title}</span>
                  {row.due_date && (
                    <span className="text-xs text-gray-500 block">
                      Due: {new Date(row.due_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              ),
            },
            {
              label: 'Target / Member',
              field: 'target_per_member',
              render: (row) => (
                row.target_per_member === 'Mixed Cumulative' ? (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">
                    Mixed Cumulative
                  </span>
                ) : (
                  <span>KES {Number(row.target_per_member || 0).toLocaleString()}</span>
                )
              ),
            },
            {
              label: detail?.subgroup?.type === 'village' ? 'Village Cumulative Target' : 'Total Subgroup Target',
              field: 'subgroup_target',
              render: (row) => <span className="font-semibold text-gray-900 dark:text-white">KES {Number(row.subgroup_target || 0).toLocaleString()}</span>,
            },
            {
              label: 'Total Paid',
              field: 'subgroup_paid',
              render: (row) => (
                <div>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400 block">
                    KES {Number(row.subgroup_paid || 0).toLocaleString()}
                  </span>
                  {Number(row.subgroup_voluntary_paid || 0) > 0 && (
                    <span className="text-[10px] font-medium text-purple-600 dark:text-purple-400 block mt-0.5">
                      Req: KES {Number(row.subgroup_required_paid || 0).toLocaleString()} | Vol: KES {Number(row.subgroup_voluntary_paid || 0).toLocaleString()}
                    </span>
                  )}
                </div>
              ),
            },
            {
              label: detail?.subgroup?.type === 'village' ? 'Village Arrears' : 'Subgroup Arrears',
              field: 'subgroup_arrears',
              render: (row) => (
                <span className={`font-bold ${row.subgroup_arrears > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-500'}`}>
                  KES {Number(row.subgroup_arrears || 0).toLocaleString()}
                </span>
              ),
            },
            {
              label: 'Compliance',
              field: 'compliance_percentage',
              render: (row) => (
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full ${row.compliance_percentage >= 100 ? 'bg-emerald-500' : row.compliance_percentage >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
                      style={{ width: `${Math.min(100, row.compliance_percentage)}%` }}
                    />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{row.compliance_percentage}%</span>
                </div>
              ),
            },
            {
              label: 'Deadline Status',
              field: 'is_overdue',
              render: (row) => (
                row.is_overdue ? (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400 animate-pulse">
                    Overdue
                  </span>
                ) : (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                    On Track
                  </span>
                )
              ),
            },
          ];

          return (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 kpi-grid">
                <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider block">Assigned Members</span>
                    <span className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1 block">{detail.members.length}</span>
                  </div>
                  <div className="p-3 rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
                    <Users size={24} />
                  </div>
                </div>

                <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider block">
                      {detail?.subgroup?.type === 'village' ? 'Mixed Cumulative Target' : 'Total Subgroup Target'}
                    </span>
                    <span className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1 block">
                      KES {Number(driveSummary.total_target || 0).toLocaleString()}
                    </span>
                    {detail?.subgroup?.type === 'village' && (
                      <span className="text-[10px] text-purple-600 dark:text-purple-400 font-medium block mt-0.5">
                        Sum of drive targets for assigned members
                      </span>
                    )}
                  </div>
                  <div className="p-3 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
                    <Layers size={24} />
                  </div>
                </div>

                <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider block">
                      {detail?.subgroup?.type === 'village' ? 'Village Total Paid' : 'Subgroup Total Paid'}
                    </span>
                    <span className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1 block">
                      KES {Number(driveSummary.total_paid || 0).toLocaleString()}
                    </span>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 font-medium mt-1 space-y-0.5">
                      <span className="block text-emerald-700 dark:text-emerald-300">
                        Required: KES {Number(driveSummary.total_required_paid || driveSummary.total_effective_paid || 0).toLocaleString()}
                      </span>
                      {Number(driveSummary.total_voluntary_paid || 0) > 0 && (
                        <span className="block text-purple-600 dark:text-purple-400">
                          Voluntary: KES {Number(driveSummary.total_voluntary_paid || 0).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-3 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400 self-start">
                    <CreditCard size={24} />
                  </div>
                </div>

                <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider block">
                      {detail?.subgroup?.type === 'village' ? 'Village Arrears' : 'Subgroup Arrears'}
                    </span>
                    <span className="text-2xl font-extrabold text-rose-600 dark:text-rose-400 mt-1 block">
                      KES {Number(driveSummary.total_arrears || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="p-3 rounded-full bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400">
                    <FileText size={24} />
                  </div>
                </div>

                <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-gray-500 font-medium uppercase tracking-wider block">Compliance Rate</span>
                    <span className={`text-2xl font-extrabold mt-1 block ${(driveSummary.overall_compliance_percentage || 0) >= 100 ? 'text-emerald-600 dark:text-emerald-400' : (driveSummary.overall_compliance_percentage || 0) >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {Number(driveSummary.overall_compliance_percentage || 0).toFixed(1)}%
                    </span>
                  </div>
                  <div className="p-3 rounded-full bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400">
                    <CheckCircle size={24} />
                  </div>
                </div>
              </div>

              {/* Drive Performance & Compliance */}
              {driveSummary.drives && driveSummary.drives.length > 0 && (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                      <Layers size={20} className="text-blue-600" />
                      <span>Subgroup Drive Targets & Arrears Performance</span>
                    </h2>
                    <span className="text-xs text-gray-500">Aggregated across all {detail.members.length} members</span>
                  </div>
                  <Table
                    columns={subgroupDriveColumns}
                    data={driveSummary.drives}
                    loading={isDetailLoading}
                  />
                </div>
              )}
            </div>
          );
        })()}

        {/* Members and Statements tabs */}
        <div className="space-y-6">
          {/* Co-Members */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                <Users size={20} className="text-gray-500" />
                <span>{tenantLabel}s</span>
              </h2>
              <button
                onClick={openAddTenant}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center space-x-1.5"
              >
                <UserPlus size={16} />
                <span>Add {tenantLabel}</span>
              </button>
            </div>
            <Table
              columns={membersColumns}
              data={detail.members}
              loading={isDetailLoading}
            />
          </div>

        </div>

        {/* Tenant create/edit modal */}
        <Modal
          isOpen={isTenantModalOpen}
          onClose={() => { setIsTenantModalOpen(false); setEditingTenant(null); }}
          title={`${editingTenant ? 'Edit' : 'Add'} ${tenantLabel}`}
        >
          <form onSubmit={handleTenantSubmit} className="space-y-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select {tenantLabel} *</label>
                {editingTenant ? (
                  <div className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700 font-medium cursor-not-allowed">
                    {editingTenant.name} ({editingTenant.phone_number})
                  </div>
                ) : (
                  <AsyncMemberSelect
                    value={tenantForm.selectedOption}
                    onChange={(option) => setTenantForm({ ...tenantForm, member_id: option?.value || '', selectedOption: option })}
                    placeholder={`Search and select a ${tenantLabel.toLowerCase()}...`}
                    isClearable
                    unassignedOnly={true}
                    required
                  />
                )}
                <p className="text-xs text-gray-500 mt-1">If the {tenantLabel.toLowerCase()} is not in this list, add them from the main Members page first.</p>
              </div>
              {editingTenant ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subgroup</label>
                  <select
                    value={tenantForm.subgroup_id || ''}
                    onChange={(e) => setTenantForm({ ...tenantForm, subgroup_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- No Subgroup (Remove) --</option>
                    {subgroups.map(sg => (
                      <option key={sg.id} value={sg.id}>{sg.name}</option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => { setIsTenantModalOpen(false); setEditingTenant(null); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm"
                disabled={updateMemberMutation.isPending}
              >
                {updateMemberMutation.isPending ? 'Saving...' : (editingTenant ? 'Save Changes' : `Assign ${tenantLabel}`)}
              </button>
            </div>
          </form>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={isEditOpen}
          onClose={() => {
            setIsEditOpen(false);
            setEditingSubgroup(null);
          }}
          title={`Edit Subgroup`}
        >
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subgroup Name/No *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Apartment A1"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subgroup Type *</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="subgroup">Subgroup / Category (e.g. Married, Single, Youth)</option>
                <option value="village">Village / Region (e.g. Central Village, North Region)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Subgroup for central region"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsEditOpen(false);
                  setEditingSubgroup(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm"
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    );
  }

  // Otherwise, render list view
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white flex items-center space-x-3">
            <Layers className="text-blue-600" size={32} />
            <span>Subgroups Management</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">
            Create, view, and manage physical grouping structures (subgroups) and consolidate statement summation balances.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={openCreateModal}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg active:scale-95"
          >
            <Plus size={20} />
            <span>Add Subgroup</span>
          </button>
        </div>
      </div>



      {isIrregular && (
        <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
          <button
            onClick={() => setActiveTab('subgroups')}
            className={`px-4 py-2 font-medium text-sm border-b-2 flex items-center space-x-2 transition-colors ${
              activeTab === 'subgroups'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Layers size={18} />
            <span>Subgroups / Categories ({subgroups.filter(s => s.type === 'subgroup' || !s.type).length})</span>
          </button>
          <button
            onClick={() => setActiveTab('villages')}
            className={`px-4 py-2 font-medium text-sm border-b-2 flex items-center space-x-2 transition-colors ${
              activeTab === 'villages'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Globe size={18} />
            <span>Villages / Regions ({subgroups.filter(s => s.type === 'village').length})</span>
          </button>
        </div>
      )}

      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={`Search subgroups by name, description, or member...`}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <Table
          columns={listColumns}
          data={paginatedSubgroups}
          loading={isListLoading}
          page={page}
          perPage={perPage}
          total={filteredSubgroups.length}
          onPageChange={setPage}
        />
      </div>

      {/* Import Modal */}
      <Modal
        isOpen={isImportOpen}
        onClose={() => { setIsImportOpen(false); setImportFile(null); }}
        title={`Bulk Import Subgroups`}
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Upload a CSV with one row per subgroup. Required columns:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside mb-3">
              <li>Name (required) — the subgroup name or number</li>
              <li>Description (optional)</li>
            </ul>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              disabled={templateMutation.isPending}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 disabled:opacity-50"
            >
              <Download size={16} />
              {templateMutation.isPending ? 'Preparing...' : 'Download Template'}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CSV File</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {importFile && (
              <p className="text-xs text-gray-500 mt-1">Selected: {importFile.name}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => { setIsImportOpen(false); setImportFile(null); }}
              disabled={importMutation.isPending}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImportSubmit}
              disabled={importMutation.isPending || !importFile}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm disabled:opacity-50"
            >
              {importMutation.isPending ? 'Uploading...' : `Import Subgroups`}
            </button>
          </div>
        </div>
      </Modal>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title={`Add New Subgroup`}
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subgroup Name/No *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Group A"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subgroup Type *</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="subgroup">Subgroup / Category (e.g. Married, Single, Youth)</option>
              <option value="village">Village / Region (e.g. Central Village, North Region)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Subgroup for central region"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={() => setIsCreateOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : `Create Subgroup`}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal (when detail view is not active but editing from list) */}
      <Modal
        isOpen={isEditOpen && !selectedId}
        onClose={() => {
          setIsEditOpen(false);
          setEditingSubgroup(null);
        }}
        title={`Edit Subgroup`}
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subgroup Name/No *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Group A"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subgroup Type *</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="subgroup">Subgroup / Category (e.g. Married, Single, Youth)</option>
              <option value="village">Village / Region (e.g. Central Village, North Region)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Subgroup for central region"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setIsEditOpen(false);
                setEditingSubgroup(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
